import React, { useState, useEffect } from 'react';
import { DollarSign, Plus, Sparkles, Loader2, TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';
import { financeAPI, exportAPI } from '../../services/api';
import AssetSelect from '../../components/AssetSelect';
import ExportMenu from '../../components/ExportMenu';
import { useAuth } from '../../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const TABS = [{ id:'pnl',label:'P&L Overview' },{ id:'capex',label:'CapEx' },{ id:'opex',label:'OpEx' },{ id:'invoices',label:'Invoices' }];

const INV_STATUS = {
  draft:     'bg-gray-100 text-gray-600',
  sent:      'bg-blue-100 text-blue-700',
  paid:      'bg-emerald-100 text-emerald-700',
  overdue:   'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

const CAPEX_STATUS = {
  planned:'bg-gray-100 text-gray-600', committed:'bg-blue-100 text-blue-700',
  in_progress:'bg-amber-100 text-amber-700', completed:'bg-emerald-100 text-emerald-700', cancelled:'bg-red-100 text-red-600'
};

const F = ({ label, children }) => <div><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>{children}</div>;
const I = (p) => <input {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30"/>;
const S = ({ children, ...p }) => <select {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 bg-white">{children}</select>;

function Modal({ title, onClose, children, onSubmit, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto">{children}</div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={onSubmit} disabled={loading} className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60">
            {loading&&<Loader2 size={14} className="animate-spin"/>} Save
          </button>
        </div>
      </div>
    </div>
  );
}

const currentYear = new Date().getFullYear();
const YEARS = Array.from({length:5},(_,i)=>currentYear-i);
const currentMonth = new Date().getMonth()+1;

export default function FinancePage() {
  const { hasRole } = useAuth();
  const canWrite = hasRole('super_admin','finance');

  const [tab, setTab]         = useState('pnl');
  const [yearFilter, setYear] = useState(String(currentYear));
  const [pnl, setPnl]         = useState(null);
  const [capex, setCapex]     = useState([]);
  const [opex, setOpex]       = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiNarrative, setAiNarrative] = useState('');
  const [aiAnomaly, setAiAnomaly]     = useState('');
  const [aiLoading, setAiLoading]     = useState(false);
  const [saving, setSaving]   = useState(false);

  const [capexModal, setCapexModal]   = useState(false);
  const [opexModal, setOpexModal]     = useState(false);
  const [invModal, setInvModal]       = useState(false);

  const initCapex = { asset_id:'', title:'', category:'', budget_amount:'', period_year: currentYear, status:'planned' };
  const initOpex  = { category:'om', description:'', budget_amount:'', actual_amount:'', period_year: currentYear, period_month: currentMonth };
  const initInv   = { invoice_number:'', invoice_type:'supplier', counterparty_name:'', amount:'', tax_amount:'0', currency:'EUR', issue_date: new Date().toISOString().split('T')[0], description:'' };
  const [cForm, setCForm] = useState(initCapex);
  const [oForm, setOForm] = useState(initOpex);
  const [iForm, setIForm] = useState(initInv);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, c, o, inv] = await Promise.all([
        financeAPI.pnl({ year: yearFilter }),
        financeAPI.capex({ year: yearFilter }),
        financeAPI.opex({ year: yearFilter }),
        financeAPI.invoices({}),
      ]);
      setPnl(p.data.data);
      setCapex(c.data.data);
      setOpex(o.data.data);
      setInvoices(inv.data.data.items || inv.data.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, [yearFilter]);

  const saveCapex = async () => { setSaving(true); try { await financeAPI.addCapex(cForm); toast.success('CapEx item added'); setCapexModal(false); loadAll(); } catch {} finally { setSaving(false); }};
  const saveOpex  = async () => { setSaving(true); try { await financeAPI.addOpex(oForm); toast.success('OpEx item added'); setOpexModal(false); loadAll(); } catch {} finally { setSaving(false); }};
  const saveInv   = async () => { setSaving(true); try { await financeAPI.addInvoice(iForm); toast.success('Invoice created'); setInvModal(false); loadAll(); } catch {} finally { setSaving(false); }};

  const markPaid = async (id) => {
    try { await financeAPI.updateInvoice(id,{ status:'paid', paid_date: new Date().toISOString().split('T')[0] }); toast.success('Invoice marked paid'); loadAll(); }
    catch {}
  };

  const generateNarrative = async () => {
    setAiLoading(true);
    try {
      const { data } = await financeAPI.boardNarrative({ year: parseInt(yearFilter), month: currentMonth });
      setAiNarrative(data.data.narrative);
      toast.success('Board narrative generated');
    } catch {} finally { setAiLoading(false); }
  };

  const runAnomalyCheck = async () => {
    setAiLoading(true);
    try {
      const { data } = await financeAPI.anomalyCheck({ year: parseInt(yearFilter), month: currentMonth });
      setAiAnomaly(data.data.analysis);
      toast.success('Anomaly check complete');
    } catch {} finally { setAiLoading(false); }
  };

  // Chart — opex by category
  const opexByCategory = opex.reduce((acc,o) => {
    acc[o.category]=(acc[o.category]||0)+parseFloat(o.actual_amount||0);
    return acc;
  },{});
  const opexChart = Object.entries(opexByCategory).map(([k,v])=>({ name:k.replace('_',' '), value: v }));

  const set = (setter) => (k) => (e) => setter(f=>({...f,[k]:e.target.value}));

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Financial Management</h1>
          <p className="text-sm text-gray-500">P&L · CapEx · OpEx · Invoicing</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={yearFilter} onChange={e=>setYear(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none">
            {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          {canWrite && <div className="flex gap-2">
            <button onClick={runAnomalyCheck} disabled={aiLoading} className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50">
              {aiLoading?<Loader2 size={14} className="animate-spin"/>:<Sparkles size={14}/>} Anomaly Check
            </button>
            <button onClick={generateNarrative} disabled={aiLoading} className="flex items-center gap-1.5 px-3 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600">
              {aiLoading?<Loader2 size={14} className="animate-spin"/>:<Sparkles size={14}/>} Board Narrative
            </button>
          </div>}
          <ExportMenu exports={[
            { label:'P&L PDF',       apiFn:()=>exportAPI.pnlPDF(yearFilter),  filename:`pnl-${yearFilter}.pdf`  },
            { label:'Invoices Excel', apiFn: exportAPI.invoicesExcel, filename:'invoices.xlsx' },
          ]}/>
        </div>
      </div>

      {/* P&L KPIs */}
      {pnl && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[['Revenue', pnl.revenue, 'bg-emerald-50','text-emerald-700'],
            ['OpEx',    pnl.opex,    'bg-amber-50', 'text-amber-700'],
            ['EBITDA',  pnl.ebitda,  pnl.ebitda>=0?'bg-blue-50':'bg-red-50', pnl.ebitda>=0?'text-blue-700':'text-danger'],
            ['CapEx',   pnl.capex,   'bg-purple-50','text-purple-700'],
          ].map(([l,v,bg,tc])=>(
            <div key={l} className={clsx('rounded-xl border border-gray-200 p-5',bg)}>
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{l}</p>
              <p className={clsx('text-xl font-bold mt-1',tc)}>€{Number(v||0).toLocaleString(undefined,{maximumFractionDigits:0})}</p>
              {l==='EBITDA'&&pnl.ebitda_margin_pct!=null&&<p className="text-xs text-gray-400 mt-0.5">{pnl.ebitda_margin_pct.toFixed(1)}% margin</p>}
            </div>
          ))}
        </div>
      )}

      {/* AI outputs */}
      {(aiNarrative||aiAnomaly) && (
        <div className="space-y-3">
          {aiNarrative && <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3"><Sparkles size={14} className="text-primary-500"/><h3 className="text-sm font-semibold text-gray-700">Board Narrative</h3></div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{aiNarrative}</p>
          </div>}
          {aiAnomaly && <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3"><Sparkles size={14} className="text-primary-500"/><h3 className="text-sm font-semibold text-gray-700">Anomaly Detection</h3></div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{aiAnomaly}</p>
          </div>}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            className={clsx('px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab===t.id?'border-primary-500 text-primary-600':'border-transparent text-gray-500 hover:text-gray-700')}>
            {t.label}
          </button>
        ))}
      </div>

      {/* P&L tab */}
      {tab==='pnl' && opexChart.length>0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">OpEx by Category (€)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={opexChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
              <XAxis dataKey="name" fontSize={11} tick={{textTransform:'capitalize'}}/>
              <YAxis fontSize={11} tickFormatter={v=>`€${(v/1000).toFixed(0)}k`}/>
              <Tooltip formatter={v=>`€${Number(v).toLocaleString()}`}/>
              <Bar dataKey="value" fill="#1D6B4E" radius={[4,4,0,0]} name="Amount"/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* CapEx */}
      {tab==='capex' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">CapEx Tracker — {yearFilter}</h3>
            {canWrite && <button onClick={()=>setCapexModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600"><Plus size={12}/>Add Item</button>}
          </div>
          {capex.length===0?<div className="text-center py-12 text-gray-400 text-sm">No CapEx items for {yearFilter}.</div>
          :<table className="w-full text-sm"><thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>{['Title','Category','Budget €','Committed €','Actual €','Status'].map(h=><th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-100">{capex.map((c,i)=>(
              <tr key={c.id} className={i%2===0?'bg-white':'bg-surface'}>
                <td className="px-4 py-3 font-medium text-gray-900">{c.title}</td>
                <td className="px-4 py-3 text-gray-500 capitalize">{c.category||'—'}</td>
                <td className="px-4 py-3">€{Number(c.budget_amount||0).toLocaleString()}</td>
                <td className="px-4 py-3">€{Number(c.committed_amount||0).toLocaleString()}</td>
                <td className="px-4 py-3 font-medium">€{Number(c.actual_amount||0).toLocaleString()}</td>
                <td className="px-4 py-3"><span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize',CAPEX_STATUS[c.status])}>{c.status?.replace('_',' ')}</span></td>
              </tr>
            ))}</tbody>
          </table>}
        </div>
      )}

      {/* OpEx */}
      {tab==='opex' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">OpEx Tracker — {yearFilter}</h3>
            {canWrite && <button onClick={()=>setOpexModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600"><Plus size={12}/>Add Item</button>}
          </div>
          {opex.length===0?<div className="text-center py-12 text-gray-400 text-sm">No OpEx items for {yearFilter}.</div>
          :<table className="w-full text-sm"><thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>{['Period','Category','Description','Budget €','Actual €','Variance'].map(h=><th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-100">{opex.map((o,i)=>{
              const variance = parseFloat(o.actual_amount||0)-parseFloat(o.budget_amount||0);
              return (<tr key={o.id} className={i%2===0?'bg-white':'bg-surface'}>
                <td className="px-4 py-3 text-gray-600">{o.period_month}/{o.period_year}</td>
                <td className="px-4 py-3 capitalize text-gray-600">{o.category?.replace('_',' ')}</td>
                <td className="px-4 py-3 text-gray-600">{o.description||'—'}</td>
                <td className="px-4 py-3">€{Number(o.budget_amount||0).toLocaleString()}</td>
                <td className="px-4 py-3 font-medium">€{Number(o.actual_amount||0).toLocaleString()}</td>
                <td className="px-4 py-3"><span className={clsx('text-xs font-medium',variance>0?'text-danger':'text-accent')}>{variance>=0?'+':''}{variance.toLocaleString(undefined,{maximumFractionDigits:0})}</span></td>
              </tr>);
            })}</tbody>
          </table>}
        </div>
      )}

      {/* Invoices */}
      {tab==='invoices' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Invoice Management</h3>
            {canWrite && <button onClick={()=>setInvModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600"><Plus size={12}/>Add Invoice</button>}
          </div>
          {invoices.length===0?<div className="text-center py-12 text-gray-400 text-sm">No invoices yet.</div>
          :<table className="w-full text-sm"><thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>{['Invoice #','Type','Counterparty','Amount €','Due Date','Status',''].map(h=><th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-100">{invoices.map((inv,i)=>(
              <tr key={inv.id} className={i%2===0?'bg-white':'bg-surface'}>
                <td className="px-4 py-3 font-mono text-xs font-medium text-gray-900">{inv.invoice_number}</td>
                <td className="px-4 py-3 capitalize text-gray-600">{inv.invoice_type}</td>
                <td className="px-4 py-3 text-gray-700">{inv.counterparty_name}</td>
                <td className="px-4 py-3 font-medium text-gray-900">€{Number(inv.total_amount).toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-600">{inv.due_date?new Date(inv.due_date).toLocaleDateString('en-GB'):'—'}</td>
                <td className="px-4 py-3"><span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize',INV_STATUS[inv.status])}>{inv.status}</span></td>
                <td className="px-4 py-3">{canWrite&&inv.status!=='paid'&&<button onClick={()=>markPaid(inv.id)} className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100">Mark Paid</button>}</td>
              </tr>
            ))}</tbody>
          </table>}
        </div>
      )}

      {/* CapEx Modal */}
      {capexModal && <Modal title="Add CapEx Item" onClose={()=>setCapexModal(false)} onSubmit={saveCapex} loading={saving}>
        <F label="Asset (optional)"><AssetSelect value={cForm.asset_id||''} onChange={e=>setCForm(f=>({...f,asset_id:e.target.value}))} placeholder="Not asset-specific"/></F>
        <F label="Title *"><I value={cForm.title} onChange={e=>setCForm(f=>({...f,title:e.target.value}))}/></F>
        <div className="grid grid-cols-2 gap-4">
          <F label="Category"><I value={cForm.category} onChange={e=>setCForm(f=>({...f,category:e.target.value}))} placeholder="e.g. Equipment"/></F>
          <F label="Status"><S value={cForm.status} onChange={e=>setCForm(f=>({...f,status:e.target.value}))}>
            {Object.entries(CAPEX_STATUS).map(([k])=><option key={k} value={k} className="capitalize">{k.replace('_',' ')}</option>)}
          </S></F>
          <F label="Budget (€)"><I type="number" value={cForm.budget_amount} onChange={e=>setCForm(f=>({...f,budget_amount:e.target.value}))}/></F>
          <F label="Year"><I type="number" value={cForm.period_year} onChange={e=>setCForm(f=>({...f,period_year:e.target.value}))}/></F>
        </div>
      </Modal>}

      {/* OpEx Modal */}
      {opexModal && <Modal title="Add OpEx Item" onClose={()=>setOpexModal(false)} onSubmit={saveOpex} loading={saving}>
        <div className="grid grid-cols-2 gap-4">
          <F label="Category"><S value={oForm.category} onChange={e=>setOForm(f=>({...f,category:e.target.value}))}>
            {['om','insurance','grid_fees','admin','land_lease','other'].map(c=><option key={c} value={c}>{c.replace('_',' ')}</option>)}
          </S></F>
          <F label="Description"><I value={oForm.description} onChange={e=>setOForm(f=>({...f,description:e.target.value}))}/></F>
          <F label="Budget (€)"><I type="number" value={oForm.budget_amount} onChange={e=>setOForm(f=>({...f,budget_amount:e.target.value}))}/></F>
          <F label="Actual (€)"><I type="number" value={oForm.actual_amount} onChange={e=>setOForm(f=>({...f,actual_amount:e.target.value}))}/></F>
          <F label="Year"><I type="number" value={oForm.period_year} onChange={e=>setOForm(f=>({...f,period_year:e.target.value}))}/></F>
          <F label="Month"><I type="number" min="1" max="12" value={oForm.period_month} onChange={e=>setOForm(f=>({...f,period_month:e.target.value}))}/></F>
        </div>
      </Modal>}

      {/* Invoice Modal */}
      {invModal && <Modal title="Add Invoice" onClose={()=>setInvModal(false)} onSubmit={saveInv} loading={saving}>
        <div className="grid grid-cols-2 gap-4">
          <F label="Invoice # *"><I value={iForm.invoice_number} onChange={e=>setIForm(f=>({...f,invoice_number:e.target.value}))}/></F>
          <F label="Type"><S value={iForm.invoice_type} onChange={e=>setIForm(f=>({...f,invoice_type:e.target.value}))}>
            <option value="supplier">Supplier</option><option value="offtaker">Offtaker</option><option value="internal">Internal</option>
          </S></F>
          <F label="Counterparty *"><I value={iForm.counterparty_name} onChange={e=>setIForm(f=>({...f,counterparty_name:e.target.value}))}/></F>
          <F label="Currency"><S value={iForm.currency} onChange={e=>setIForm(f=>({...f,currency:e.target.value}))}>
            <option value="EUR">EUR</option><option value="GBP">GBP</option><option value="USD">USD</option>
          </S></F>
          <F label="Amount"><I type="number" step="0.01" value={iForm.amount} onChange={e=>setIForm(f=>({...f,amount:e.target.value}))}/></F>
          <F label="Tax Amount"><I type="number" step="0.01" value={iForm.tax_amount} onChange={e=>setIForm(f=>({...f,tax_amount:e.target.value}))}/></F>
          <F label="Issue Date"><I type="date" value={iForm.issue_date} onChange={e=>setIForm(f=>({...f,issue_date:e.target.value}))}/></F>
        </div>
        <F label="Description"><textarea value={iForm.description} onChange={e=>setIForm(f=>({...f,description:e.target.value}))} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500/30"/></F>
      </Modal>}
    </div>
  );
}
